import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, CheckCircle2, Minus } from "lucide-react";
import type { WizardState } from "@/lib/newPropertyWizard";
import { getChecklistTemplate } from "@/data/checklistTemplates";

interface Step8DoneProps {
  state: WizardState;
  onViewProperty: () => void;
  onAddAnother: () => void;
}

function StatusLine({
  done,
  skipped,
  label,
}: {
  done: boolean;
  skipped: boolean;
  label: React.ReactNode;
}) {
  if (skipped) {
    return (
      <li className="flex items-start gap-2 text-sm text-muted-foreground">
        <Minus className="h-4 w-4 shrink-0 mt-0.5" />
        <span>{label} — Skipped</span>
      </li>
    );
  }
  if (done) {
    return (
      <li className="flex items-start gap-2 text-sm">
        <Check className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
        <span>{label}</span>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-2 text-sm text-muted-foreground">
      <Minus className="h-4 w-4 shrink-0 mt-0.5" />
      <span>{label}</span>
    </li>
  );
}

export function Step8Done({ state, onViewProperty, onAddAnother }: Step8DoneProps) {
  const template = state.selectedTemplate
    ? getChecklistTemplate(state.selectedTemplate)
    : null;

  return (
    <div className="space-y-8 py-4 text-center max-w-lg mx-auto">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="flex justify-center"
      >
        <CheckCircle2 className="h-20 w-20 text-green-600" />
      </motion.div>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Your property is ready!</h1>
        <p className="text-muted-foreground text-sm mt-2">
          Here is what we set up for {state.name || "your property"}.
        </p>
      </div>

      <Card className="text-left">
        <CardContent className="pt-6">
          <ul className="space-y-3">
            <StatusLine
              done
              skipped={false}
              label={<>Property created: <strong>{state.name}</strong></>}
            />
            <StatusLine
              done={state.rooms.length > 0}
              skipped={false}
              label={<>{state.rooms.length} room{state.rooms.length !== 1 ? "s" : ""} set up</>}
            />
            <StatusLine
              done={state.checklistDone}
              skipped={state.checklistSkipped}
              label={
                state.checklistDone && template ? (
                  <>
                    Checklist: {template.name} with {state.checklistTaskCount} tasks
                  </>
                ) : (
                  "Checklist"
                )
              }
            />
            <StatusLine
              done={state.inventoryDone}
              skipped={state.inventorySkipped}
              label={
                state.inventoryDone ? (
                  <>Inventory: {state.inventoryItemCount} items</>
                ) : (
                  "Inventory"
                )
              }
            />
            <StatusLine
              done={state.calendarDone}
              skipped={state.calendarSkipped}
              label="Calendar connected"
            />
            <StatusLine
              done={state.teamDone}
              skipped={state.teamSkipped}
              label={
                state.teamDone ? (
                  <>Team: {state.teamInviteCount} invite{state.teamInviteCount !== 1 ? "s" : ""} sent</>
                ) : (
                  "Team invited"
                )
              }
            />
          </ul>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button type="button" size="lg" onClick={onViewProperty}>
          View Property
        </Button>
        <Button type="button" variant="outline" size="lg" onClick={onAddAnother}>
          Add another property
        </Button>
      </div>
    </div>
  );
}
